//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useState, useContext } from 'react';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import { Add, Delete, Save, toggleOn, toggleOff } from '@mui/icons-material';

// === COMPONENTS ===
import { Spacer            } from 'components/Spacer.jsx';
import { Expression        } from './components/Expression/Expression';
import { ExpressionContext } from '../ExpressionContext/ExpressionContext';
import { GroupDrag         } from 'components/DragDrop/GroupDrag';
import { DropProvider      } from 'components/DragDrop/DropContext';

// === UTILS ===
import { getHexAlpha } from 'components/styles.utils';

//#endregion

//-----------------------------------------------------------------
export function ExpressionView(props) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { column, row } = props;

  const theme = useTheme();

  const { expressions, createExpression, createExpressions, moveExpression, templates } = useContext( ExpressionContext );

  const [ is_open , setIsOpen ] = useState( false );
  
  const is_light = theme.palette.mode === 'light';

  //#endregion

  //#region [STYLES]

  const _styles = {
    container: {
      gridColumn    : column || 2,
      gridRow       : row || 2,
      height        : '100%'   ,
      width         : '100%'   ,
      borderRadius  : '1rem'   ,
      overflow      : 'hidden' ,
      padding       : '1rem'   , 
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    container_exp: {
      height       : 'calc( 100% - 4rem )' ,
      width        : '100%'   ,
      overflow     : 'hidden' , 
      borderRadius : '1rem'   ,
    },
    container_exp_2: {
      width        : '100%' ,
      height       : 'max-content' ,
      maxHeight    : '100%',
      overflow     : 'auto' ,
      borderRadius : '1rem'   ,
      padding      : '0.5rem', 
      paddingBottom: '0rem',
      scrollbarColor: `${theme.palette.secondary.main} ${theme.palette.background.primary[theme.palette.mode]}`,
      scrollbarWidth: 'thin',
    },
    header: {
      fontSize: '2rem'
    },
    border_1: {
      display: 'flex',
      borderRadius: '1rem',
      width: '100%',
      position: 'relative',
    },
    button_add: {
      backgroundColor: theme.palette.primary[theme.palette.mode],
      color: 'white',
    },
    button_add_template: {
      width: '12rem',
      backgroundColor: theme.palette.primary[theme.palette.mode],
      color: 'white',
    },
    button_toggle: {
      backgroundColor: theme.palette.primary[theme.palette.mode],
      color: 'white',
    },
    dropdown: {
      display : is_open ? '' : 'none',
      width   : '300px',
      top     : '45px',
      right   : '40px',
      position: 'absolute',
      zIndex  : 80,
      borderRadius: '0rem 0.5rem 0.5rem 0.5rem',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    }
  }

  const CONTAINER = {
    style: _styles.container,
  };
  const CONTAINER_EXP = {
    style: _styles.container_exp,
  };
  const CONTAINER_EXP_2 = {
    style: _styles.container_exp_2,
  };
  const HEADER = {
    style: _styles.header,
    children: 'Expression:',
  };
  const BORDER_1 = {
    style: _styles.border_1,
  };
  const BUTTON_ADD = {
    sx: _styles.button_add,
    children: <Add />,
    onClick: onClick_Add,
  };
  const BUTTON_ADD_TEMPLATE = {
    sx: _styles.button_add_template,
    children: [<Add />, 'Template'],
    onClick: ()=>setIsOpen(!is_open),
  };
    
  const SELECT_FIELD = {
    sx          : _styles.select_field,
    id          : 'template-select',
    options     : templates.map( x=> { return { label: x.description, value: x } }),
    value       : null,
    size        : 'large',
    onChange    : onChange_Template,
    renderInput : (params) => <TextField {...params} label="Template" />,
  };
    
  //#endregion

  //#region [EFFECT]

  //#endregion

  //#region [MODIFY]

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onClick_Add() {
    //-----------------------------------------------------------------
    createExpression();
  }
  
  //-----------------------------------------------------------------
  function onChange_Template( event, value ) {
    //-----------------------------------------------------------------
    const new_values = value.value.rule.RULE.values;
    createExpressions( new_values );
    setIsOpen( false );
  }

  //#endregion

  //#region [DISPLAY]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <div { ...CONTAINER } >
        <div { ...BORDER_1 } >
          <div { ...HEADER } />
          <Spacer fill />
          <Tooltip title='Add existing expression' placement='top'>
            <Button { ...BUTTON_ADD_TEMPLATE  } />
          </Tooltip>
          <Box sx={_styles.dropdown} >
            <Autocomplete { ...SELECT_FIELD } />
          </Box>
          <Spacer />
          <Tooltip title='Add item' placement='top'>
            <IconButton { ...BUTTON_ADD  } />
          </Tooltip>
        </div>
        <Spacer />
        { displayExpressions() }
      </div>
    );
  }

  //-----------------------------------------------------------------
  function displayExpressions() {
    //-----------------------------------------------------------------
    const comps = expressions.map( (exp,index) => {
      return (
        <GroupDrag key={exp.id} data_type={'expression'} data={{id: exp.id, index: index}} styles={[]} moveItem={moveExpression}>
          <Expression data={exp} />
          <Spacer size='0.5rem' />
        </GroupDrag>
      )
    });
    return (
      <div { ...CONTAINER_EXP } >
        <div { ...CONTAINER_EXP_2 } >
          <DropProvider>
            {comps}
          </DropProvider>
        </div>
      </div>
    )
  }

  //#endregion

  return display();
}
